import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, documentId, getCountFromServer, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../../AuthContext";
import ActivityTable from "./ActivityTable.jsx";
import TestReportButton from "./TestReportButton.jsx";

// Anyone whose role isn't one of these dashboard roles counts as an app
// customer for this breakdown — owner/charity/admin accounts are excluded
// since they'd otherwise skew "customers by city" toward wherever Jamie's
// own test accounts happen to be set.
const DASHBOARD_ROLES = ["owner", "charity", "admin"];

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI",
  "SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

// Names/addresses for just these bar ids (30 per request). Never load the
// whole bars list: it's every bar in the US.
async function loadBarsById(ids) {
  const out = {};
  for (let i = 0; i < ids.length; i += 30) {
    const snap = await getDocs(query(collection(db, "bars"), where(documentId(), "in", ids.slice(i, i + 30))));
    snap.docs.forEach((d) => (out[d.id] = d.data()));
  }
  return out;
}

// How many bars per state, counted on the server (cheap; downloads no bars).
async function countBarsByState() {
  const bars = collection(db, "bars");
  const [total, ...perState] = await Promise.all([
    getCountFromServer(bars),
    ...STATES.map((st) => getCountFromServer(query(bars, where("state", "==", st)))),
  ]);
  const list = STATES.map((st, i) => ({ city: st, count: perState[i].data().count })).filter((x) => x.count > 0);
  const known = list.reduce((sum, x) => sum + x.count, 0);
  const unknown = total.data().count - known;
  if (unknown > 0) list.push({ city: "No state saved", count: unknown });
  return list.sort((a, b) => b.count - a.count);
}

export default function AdminDashboard() {
  const { user, role } = useAuth();
  const [loading, setLoading] = useState(true);
  const [barFavorites, setBarFavorites] = useState([]);
  const [barActivity, setBarActivity] = useState([]);
  const [customersByCity, setCustomersByCity] = useState([]);
  const [barsByCity, setBarsByCity] = useState([]);

  useEffect(() => {
    if (role !== "admin") return;
    loadStats();
  }, [role]);

  const loadStats = async () => {
    setLoading(true);

    const [usersSnap, eventsSnap, notificationsSnap, barsByStateList] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "events")),
      getDocs(collection(db, "notifications")),
      countBarsByState(),
    ]);

    // Favorite counts: tally every barId across every user's favoriteBars array.
    const favoriteCounts = {};
    const cityCustomerCounts = {};
    usersSnap.docs.forEach((d) => {
      const data = d.data();
      const favs = Array.isArray(data.favoriteBars) ? data.favoriteBars : [];
      favs.forEach((barId) => {
        favoriteCounts[barId] = (favoriteCounts[barId] || 0) + 1;
      });

      // Only count this user toward the customer city breakdown if they're
      // not one of the dashboard account types.
      if (!DASHBOARD_ROLES.includes(data.role)) {
        const city = data.city && data.city.trim() ? data.city.trim() : "Unknown";
        cityCustomerCounts[city] = (cityCustomerCounts[city] || 0) + 1;
      }
    });

    // Posting activity: count events + notifications per bar.
    const activityCounts = {};
    eventsSnap.docs.forEach((d) => {
      const barId = d.data().barId;
      if (!barId) return;
      activityCounts[barId] = (activityCounts[barId] || 0) + 1;
    });
    notificationsSnap.docs.forEach((d) => {
      const barId = d.data().barId;
      if (!barId) return;
      activityCounts[barId] = (activityCounts[barId] || 0) + 1;
    });

    // Names for only the bars that show up in the lists below.
    const neededIds = [...new Set([...Object.keys(favoriteCounts), ...Object.keys(activityCounts)])];
    const barData = await loadBarsById(neededIds);
    const barNameById = {};
    const barAddressById = {};
    Object.entries(barData).forEach(([id, data]) => {
      barNameById[id] = data.name || "Unnamed Bar";
      barAddressById[id] = data.address || "";
    });

    const favoritesList = Object.entries(favoriteCounts)
      .map(([barId, count]) => ({
        barId,
        name: barNameById[barId] || "(unclaimed bar)",
        address: barAddressById[barId] || "",
        count,
      }))
      .sort((a, b) => b.count - a.count);

    const activityList = Object.entries(activityCounts)
      .map(([barId, count]) => ({
        barId,
        name: barNameById[barId] || "(unclaimed bar)",
        count,
      }))
      .sort((a, b) => b.count - a.count);

    const customersByCityList = Object.entries(cityCustomerCounts)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count);

    const barsByCityList = barsByStateList;

    setBarFavorites(favoritesList);
    setBarActivity(activityList);
    setCustomersByCity(customersByCityList);
    setBarsByCity(barsByCityList);
    setLoading(false);
  };

  const sectionStyle = {
    background: "#0D0B1A",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
  };
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  };

  if (!user) {
    return <p style={{ color: "#fff" }}>Loading…</p>;
  }

  if (role !== "admin") {
    return (
      <p style={{ color: "#fff" }}>
        You don't have access to this page.
      </p>
    );
  }

  if (loading) {
    return <p style={{ color: "#fff" }}>Loading stats…</p>;
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "6px" }}>Admin Overview</h2>
      <p style={{ marginBottom: "24px", color: "#B8B0C8" }}>
        Internal stats — not visible to bar owners or charities.
      </p>

      <TestReportButton />

      <ActivityTable />

      <div style={sectionStyle}>
        <h3 style={{ marginBottom: "12px" }}>Most Favorited Bars</h3>
        {barFavorites.length === 0 ? (
          <p style={{ color: "#B8B0C8" }}>No favorites yet.</p>
        ) : (
          barFavorites.slice(0, 25).map((b) => (
            <div key={b.barId} style={rowStyle}>
              <div>
                <div>{b.name}</div>
                <div style={{ fontSize: "15px", color: "#8A84A0" }}>{b.address}</div>
              </div>
              <div style={{ fontWeight: 700, color: "#F472B6" }}>{b.count} ♥</div>
            </div>
          ))
        )}
      </div>

      <div style={sectionStyle}>
        <h3 style={{ marginBottom: "12px" }}>Most Active Bars (events + posts)</h3>
        {barActivity.length === 0 ? (
          <p style={{ color: "#B8B0C8" }}>No activity yet.</p>
        ) : (
          barActivity.slice(0, 25).map((b) => (
            <div key={b.barId} style={rowStyle}>
              <div>{b.name}</div>
              <div style={{ fontWeight: 700 }}>{b.count}</div>
            </div>
          ))
        )}
      </div>

      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ ...sectionStyle, flex: 1, minWidth: "280px" }}>
          <h3 style={{ marginBottom: "12px" }}>App Customers by City</h3>
          <p style={{ fontSize: "15px", color: "#8A84A0", marginTop: "-6px", marginBottom: "12px" }}>
            Excludes bar owner, charity, and admin accounts.
          </p>
          {customersByCity.length === 0 ? (
            <p style={{ color: "#B8B0C8" }}>No customer city data yet.</p>
          ) : (
            customersByCity.map((c) => (
              <div key={c.city} style={rowStyle}>
                <div>{c.city}</div>
                <div style={{ fontWeight: 700 }}>{c.count}</div>
              </div>
            ))
          )}
        </div>

        <div style={{ ...sectionStyle, flex: 1, minWidth: "280px" }}>
          <h3 style={{ marginBottom: "12px" }}>Bars by State</h3>
          <p style={{ fontSize: "15px", color: "#8A84A0", marginTop: "-6px", marginBottom: "12px" }}>
            Every bar in the app, counted by state.
          </p>
          {barsByCity.length === 0 ? (
            <p style={{ color: "#B8B0C8" }}>No bar data yet.</p>
          ) : (
            barsByCity.map((c) => (
              <div key={c.city} style={rowStyle}>
                <div>{c.city}</div>
                <div style={{ fontWeight: 700 }}>{c.count}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
